import { supabase } from "./supabase";

// Mirror of src/lib/households.ts in the RN project. Pure Supabase queries,
// platform-neutral, copy kept in sync by hand.

export interface Household {
  id: string;
  name: string;
  code: string;
  created_by: string | null;
  created_at: string;
}

const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

function generateCode(length = 6): string {
  let out = "";
  for (let i = 0; i < length; i++) {
    out += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
  }
  return out;
}

export async function fetchMyHouseholds(): Promise<Household[]> {
  const { data, error } = await supabase
    .from("household_members")
    .select("household:households(*)")
    .order("joined_at", { ascending: true });
  if (error) throw error;
  // The Supabase type gen treats `households(*)` FK joins as an array even
  // when the foreign key is nullable one-to-one; cast through `unknown` to
  // the shape we actually get at runtime.
  return ((data || []) as unknown as Array<{ household: Household | null }>)
    .map((row) => row.household)
    .filter((h): h is Household => h !== null);
}

export async function createHousehold(name: string): Promise<Household> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not signed in");

  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateCode();
    const { data, error } = await supabase
      .from("households")
      .insert({ name, code, created_by: user.user.id })
      .select()
      .single();
    if (error) {
      if (error.code === "23505") continue; // unique violation → retry
      throw error;
    }

    const { error: memberError } = await supabase
      .from("household_members")
      .insert({ household_id: data.id, user_id: user.user.id });
    if (memberError) throw memberError;

    return data;
  }
  throw new Error("Could not generate a unique household code. Try again.");
}

export async function joinHousehold(code: string): Promise<Household> {
  const normalized = code.trim().toUpperCase();

  const { data: household, error: findError } = await supabase
    .from("households")
    .select("*")
    .eq("code", normalized)
    .maybeSingle();
  if (findError) throw findError;
  if (!household) throw new Error("Household not found. Check the code.");

  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not signed in");

  const { error: memberError } = await supabase
    .from("household_members")
    .insert({ household_id: household.id, user_id: user.user.id });
  if (memberError && memberError.code !== "23505") throw memberError;

  return household;
}

export async function leaveHousehold(householdId: string): Promise<void> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error("Not signed in");

  const { error } = await supabase
    .from("household_members")
    .delete()
    .eq("household_id", householdId)
    .eq("user_id", user.user.id);
  if (error) throw error;
}

export async function removeMember(
  householdId: string,
  userId: string
): Promise<void> {
  const { error } = await supabase
    .from("household_members")
    .delete()
    .eq("household_id", householdId)
    .eq("user_id", userId);
  if (error) throw error;
}
